import { useEffect, useRef, useState } from 'react';
import Icon from './Icon';

export default function ImageViewer({ photos = [], index = 0, open, onClose, alt = '', onIndexChange }) {
  const [current, setCurrent] = useState(index);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const pinch = useRef(null);

  useEffect(() => {
    if (open) {
      setCurrent(index);
      setScale(1);
      setOffset({ x: 0, y: 0 });
    }
  }, [open, index]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose, photos.length, current]);

  if (!open || !photos.length) return null;

  const resetZoom = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  const go = (delta) => {
    resetZoom();
    setCurrent((i) => {
      const next = Math.min(photos.length - 1, Math.max(0, i + delta));
      onIndexChange?.(next);
      return next;
    });
  };

  const select = (i) => {
    resetZoom();
    setCurrent(i);
    onIndexChange?.(i);
  };

  const onWheel = (e) => {
    e.preventDefault();
    const next = Math.min(4, Math.max(1, scale + (e.deltaY < 0 ? 0.15 : -0.15)));
    setScale(next);
    if (next === 1) setOffset({ x: 0, y: 0 });
  };

  const onPointerDown = (e) => {
    if (scale <= 1) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
  };

  const onPointerMove = (e) => {
    if (!drag.current) return;
    setOffset({
      x: e.clientX - drag.current.x,
      y: e.clientY - drag.current.y,
    });
  };

  const onPointerUp = () => {
    drag.current = null;
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [a, b] = e.touches;
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      pinch.current = { dist, scale };
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinch.current) {
      e.preventDefault();
      const [a, b] = e.touches;
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const next = Math.min(4, Math.max(1, (pinch.current.scale * dist) / pinch.current.dist));
      setScale(next);
      if (next === 1) setOffset({ x: 0, y: 0 });
    }
  };

  return (
    <div className="image-viewer" role="dialog" aria-modal="true" aria-label="Photo viewer">
      <button type="button" className="image-viewer-scrim" aria-label="Close" onClick={onClose} />
      <div className="image-viewer-panel">
        <header className="image-viewer-bar">
          <span className="image-viewer-count">
            {current + 1} <span aria-hidden="true">/</span> {photos.length}
          </span>
          <div className="image-viewer-bar-actions">
            {scale > 1 && (
              <button type="button" className="image-viewer-chip" onClick={resetZoom}>
                Reset zoom
              </button>
            )}
            <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
              <Icon name="close" />
            </button>
          </div>
        </header>

        <div
          className="image-viewer-stage"
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
        >
          {photos.length > 1 && (
            <button
              type="button"
              className="image-nav prev"
              onClick={() => go(-1)}
              disabled={current === 0}
              aria-label="Previous photo"
            >
              <Icon name="chevronLeft" />
            </button>
          )}
          <img
            src={photos[current]}
            alt={alt}
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              cursor: scale > 1 ? 'grab' : 'zoom-in',
            }}
            onDoubleClick={() => {
              if (scale > 1) resetZoom();
              else setScale(2);
            }}
            draggable={false}
          />
          {photos.length > 1 && (
            <button
              type="button"
              className="image-nav next"
              onClick={() => go(1)}
              disabled={current === photos.length - 1}
              aria-label="Next photo"
            >
              <Icon name="chevron" />
            </button>
          )}
        </div>

        {photos.length > 1 && (
          <div className="image-viewer-thumbs">
            {photos.map((src, i) => (
              <button
                key={src + i}
                type="button"
                className={i === current ? 'active' : ''}
                onClick={() => select(i)}
              >
                <img src={src} alt="" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
